import React from "react";
import Joyride, { Step, CallBackProps, TooltipRenderProps } from "react-joyride";
import {
  Box,
  Button,
  Typography,
  Paper,
  IconButton,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

interface TutorialWrapperProps {
  run: boolean;
  steps: Step[];
  callback: (data: CallBackProps) => void;
  continuous?: boolean;
  showProgress?: boolean;
  showSkipButton?: boolean;
  disableOverlayClose?: boolean;
  spotlightClicks?: boolean;
}

// Custom tooltip component with Material-UI styling
const CustomTooltip: React.FC<TooltipRenderProps> = ({
  continuous,
  index,
  step,
  backProps,
  closeProps,
  primaryProps,
  tooltipProps,
  size,
  isLastStep,
}) => {
  const theme = useTheme();

  return (
    <Paper
      {...tooltipProps}
      elevation={8}
      sx={{
        maxWidth: 400,
        minWidth: 280,
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          {step.title}
        </Typography>
        <IconButton
          {...closeProps}
          size="small"
          sx={{ ml: 1 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ p: 2 }}>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {step.content}
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {index + 1} of {size}
          </Typography>

          <Box sx={{ display: "flex", gap: 1 }}>
            {index > 0 && (
              <Button
                onClick={backProps.onClick}
                size="small"
                variant="outlined"
              >
                Back
              </Button>
            )}
            {continuous && (
              <Button
                onClick={primaryProps.onClick}
                size="small"
                variant="contained"
                color="primary"
                disableFocusRipple
              >
                {isLastStep ? "Finish" : "Next"}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

const TutorialWrapper: React.FC<TutorialWrapperProps> = ({
  run,
  steps,
  callback,
  continuous = true,
  showProgress = true,
  showSkipButton = true,
  disableOverlayClose = false,
  spotlightClicks = true,
}) => {
  const theme = useTheme();

  if (steps.length === 0) {
    return null;
  }

  return (
    <Joyride
      run={run}
      steps={steps}
      callback={callback}
      continuous={continuous}
      showProgress={showProgress}
      showSkipButton={showSkipButton}
      disableOverlayClose={disableOverlayClose}
      spotlightClicks={spotlightClicks}
      tooltipComponent={CustomTooltip}
      scrollOffset={100}
      scrollToFirstStep={true}
      styles={{
        options: {
          primaryColor: theme.palette.primary.main,
          zIndex: 10000,
          arrowColor: theme.palette.background.paper,
          backgroundColor: theme.palette.background.paper,
          textColor: theme.palette.text.primary,
          overlayColor: "rgba(0, 0, 0, 0.5)",
        },
        spotlight: {
          borderRadius: 8,
        },
        overlay: {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        },
      }}
      floaterProps={{
        styles: {
          floater: {
            filter: "drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2))",
          },
        },
      }}
    />
  );
};

export default TutorialWrapper;
